import React, { useState, useEffect } from 'react';
import { Button, Modal, Input } from '@/components/ui';
import type { ProjectZone, ZoneStatus, ZoneType } from '../types/zones.types';
import { useZones } from '../hooks/useMaintenanceZones';
import { useProjects } from '@/modules/projects/shared/hooks/useProjects';
import { useMaintenanceContracts } from '@/modules/contracts/maintenance/hooks/useMaintenanceContracts';
import { useUsers } from '@/modules/users/list/hooks/useUserList';
import { toast } from 'sonner';

interface ZoneFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  zone?: ProjectZone | null;
  companyId?: string;
  projectId?: string;
  parentZoneId?: string;
}

export function ZoneFormModal({ isOpen, onClose, zone, companyId, projectId, parentZoneId }: ZoneFormModalProps) {
  const isEdit = !!zone;
  const activeCompanyId = companyId || '';
  const { createZone, updateZone, isCreating, isUpdating } = useZones(activeCompanyId);
  const { projects = [] } = useProjects(activeCompanyId);
  const { contracts = [] } = useMaintenanceContracts(companyId);
  const { data: users = [] } = useUsers(companyId);

  const [formData, setFormData] = useState<Partial<ProjectZone>>({
    zone_code: '',
    zone_name: '',
    project_id: '',
    contract_id: '',
    zone_type: 'lawn',
    area_m2: 0,
    status_code: 'active',
    manager_user_id: '',
    description: '',
  });

  useEffect(() => {
    if (isOpen) {
      if (zone) {
        setFormData(zone);
      } else {
        setFormData({
          zone_code: '',
          zone_name: '',
          project_id: '',
          contract_id: '',
          zone_type: 'lawn',
          area_m2: 0,
          status_code: 'active',
          manager_user_id: '',
          description: '',
          company_id: companyId,
        });
      }
    }
  }, [isOpen, zone, companyId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.project_id) {
      toast.error('Vui lòng chọn dự án cho khu vực');
      return;
    }
    try {
      if (isEdit && zone) {
        await updateZone({ id: zone.id, updates: formData });
      } else {
        await createZone({ ...formData, company_id: companyId });
      }
      onClose();
      toast.success(isEdit ? 'Cập nhật khu vực thành công' : 'Khởi tạo khu vực mới thành công');
    } catch (error: any) {
      toast.error('Lỗi: ' + error.message);
    }
  };

  const isPending = isCreating || isUpdating;

  // Filter users for Foreman / Manager
  const managers = users.filter(u => ['site_foreman', 'supervisor', 'pm', 'site_manager'].includes(u.role_code || ''));

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={isEdit ? 'Cập nhật Khu vực' : 'Khởi tạo Khu vực (Zone) mới'} size="xl">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Mã khu vực *"
            required
            value={formData.zone_code || ''}
            onChange={(e) => setFormData({ ...formData, zone_code: e.target.value })}
            placeholder="VD: ZONE-01-A"
          />
          <Input
            label="Tên khu vực *"
            required
            value={formData.zone_name || ''}
            onChange={(e) => setFormData({ ...formData, zone_name: e.target.value })}
            placeholder="VD: Công viên trung tâm - Khu cỏ 1"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Dự án *</label>
            <select
              required
              className="w-full rounded-xl border-slate-200 focus:border-os-primary focus:ring-os-primary text-sm p-2.5 transition-all"
              value={formData.project_id || ''}
              onChange={(e) => setFormData({ ...formData, project_id: e.target.value })}
            >
              <option value="">-- Chọn dự án --</option>
              {projects.map(p => (
                <option key={p.id} value={p.id}>{p.name} ({p.code})</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Hợp đồng bảo dưỡng</label>
            <select
              className="w-full rounded-xl border-slate-200 focus:border-os-primary focus:ring-os-primary text-sm p-2.5 transition-all"
              value={formData.contract_id || ''}
              onChange={(e) => setFormData({ ...formData, contract_id: e.target.value })}
            >
              <option value="">-- Chọn hợp đồng --</option>
              {contracts.filter(c => c.project_id === formData.project_id).map(c => (
                <option key={c.id} value={c.id}>{c.contract_name} ({c.contract_code})</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Loại khu vực</label>
            <select
              className="w-full rounded-xl border-slate-200 focus:border-os-primary focus:ring-os-primary text-sm p-2.5 transition-all"
              value={formData.zone_type || 'lawn'}
              onChange={(e) => setFormData({ ...formData, zone_type: e.target.value as ZoneType })}
            >
              <option value="lawn">Cỏ (Lawn)</option>
              <option value="tree">Cây bóng mát (Tree)</option>
              <option value="shrub">Cây bụi (Shrub)</option>
              <option value="mixed">Hỗn hợp (Mixed)</option>
              <option value="water">Mặt nước (Water)</option>
              <option value="lighting">Hệ thống đèn (Lighting)</option>
              <option value="infrastructure">Hạ tầng (Infra)</option>
            </select>
          </div>
          <Input
            label="Diện tích (m²)"
            type="number"
            value={formData.area_m2 || ''}
            onChange={(e) => setFormData({ ...formData, area_m2: Number(e.target.value) })}
            placeholder="0"
          />
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Trạng thái</label>
            <select
              className="w-full rounded-xl border-slate-200 focus:border-os-primary focus:ring-os-primary text-sm p-2.5 transition-all"
              value={formData.status_code || 'active'}
              onChange={(e) => setFormData({ ...formData, status_code: e.target.value as ZoneStatus })}
            >
              <option value="draft">Bản thảo</option>
              <option value="active">Đang hoạt động</option>
              <option value="maintenance">Đang bảo trì</option>
              <option value="inactive">Ngưng hoạt động</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1.5">Tổ trưởng phụ trách (Foreman)</label>
          <select
            className="w-full rounded-xl border-slate-200 focus:border-os-primary focus:ring-os-primary text-sm p-2.5 transition-all"
            value={formData.manager_user_id || ''}
            onChange={(e) => setFormData({ ...formData, manager_user_id: e.target.value })}
          >
            <option value="">-- Chưa gán phụ trách --</option>
            {managers.map(user => (
              <option key={user.id} value={user.id}>{user.full_name} ({user.job_title || 'Foreman'})</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1.5">Ghi chú / Mô tả</label>
          <textarea
            className="w-full rounded-xl border-slate-200 focus:border-os-primary focus:ring-os-primary text-sm p-2.5 min-h-[80px]"
            value={formData.description || ''}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Mô tả ranh giới, đặc điểm khu vực..."
          />
        </div>

        <div className="pt-4 flex justify-end gap-3 border-t border-slate-100">
          <Button variant="outline" type="button" onClick={onClose} disabled={isPending}>
            Hủy
          </Button>
          <Button variant="primary" type="submit" isLoading={isPending}>
            {isEdit ? 'Lưu thay đổi' : 'Khởi tạo khu vực'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
